/**
 * Utility helpers shared across the Network module.
 */

/**
 * Compute days since the user last touched a contact. Returns null if
 * `last_contacted_at` is missing (i.e. they've never logged a touchpoint).
 */
export function daysSinceContact(lastIso) {
  if (!lastIso) return null;
  try {
    const last = new Date(lastIso).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - last) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

/**
 * Map a contact's staleness (days since touch) → a Resmo-branded color
 * palette used by both the grid and the Career Path views.
 *   ≤14d   = healthy green
 *   15–30d = mustard ("check in soon")
 *   31–60d = amber ("overdue")
 *    >60d  = red ("cold")
 *   never  = navy (default — needs first touch)
 */
export function stalenessTint(days) {
  if (days == null) return { color: "#0F1B2D", label: "Never touched", level: "never" };
  if (days <= 14) return { color: "#10B981", label: `${days}d ago`, level: "fresh" };
  if (days <= 30) return { color: "#FFC145", label: `${days}d ago`, level: "warming" };
  if (days <= 60) return { color: "#F59E0B", label: `${days}d ago — overdue`, level: "overdue" };
  return { color: "#EF4444", label: `${days}d ago — cold`, level: "cold" };
}

/**
 * Bucket a fit score (0–100) into a Career Path zone. Stones without a
 * fit_score (i.e. unanalyzed) sit in the special "unranked" rail.
 */
export const PATH_ZONES = [
  { id: "cold",    label: "Cold prospect", min: 0,  max: 39, accent: "#94A3B8" },
  { id: "warming", label: "Warming up",    min: 40, max: 59, accent: "#FFC145" },
  { id: "active",  label: "Active",        min: 60, max: 79, accent: "#FF6A00" },
  { id: "allies",  label: "Allies",        min: 80, max: 100, accent: "#10B981" },
];

export function zoneForScore(score) {
  if (score == null) return null;
  return PATH_ZONES.find((z) => score >= z.min && score <= z.max) || PATH_ZONES[PATH_ZONES.length - 1];
}
